/**
 * Muadil koku arama.
 * Tek arama kutusu iki listeyi birden tarar: stoklu (hizli kargo) ve ozel uretim (min 150ml).
 * Veri ilk etkilesimde tembel yuklenir; anasayfa acilisini yavaslatmaz.
 */
class KokuArama extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-koku-input]');
    this.panel = this.querySelector('[data-koku-sonuclar]');
    this.canli = this.querySelector('[data-koku-canli]');
    this.temizleBtn = this.querySelector('[data-koku-temizle]');
    this.veri = null;
    this.yukleniyor = null;
    this.secili = -1;
    this.zamanlayici = 0;
    this.sira = 0;
  }

  connectedCallback() {
    this.input.addEventListener('input', () => this.girdi());
    this.input.addEventListener('focus', () => this.veriYukle().catch(() => {}));
    this.input.addEventListener('keydown', (e) => this.klavye(e));
    this.temizleBtn.addEventListener('click', () => this.temizle());
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.kapat();
    });
  }

  /* ---------- Veri ---------- */

  veriYukle() {
    if (this.yukleniyor) return this.yukleniyor;
    this.yukleniyor = fetch(this.dataset.veriUrl)
      .then((r) => {
        if (!r.ok) throw new Error('Koku listesi yuklenemedi: HTTP ' + r.status);
        return r.json();
      })
      .then((j) => {
        const hazirla = (liste, stokta) =>
          liste.map((x) => ({ ...x, stokta, ...KokuArama.anahtarlar(x) }));
        this.veri = [...hazirla(j.stock, true), ...hazirla(j.custom, false)];
        return this.veri;
      })
      .catch((err) => {
        console.error('[koku-arama]', err);
        this.veri = null;
        this.yukleniyor = null; // sonraki denemede bastan dene
        throw err;
      });
    return this.yukleniyor;
  }

  /**
   * Bir kaydin arama anahtarlari. koku-secici.js ve koku-listesi.js de bunu kullanir.
   *   w  : kelimeler ("L'Interdit" -> ["l", "interdit"])
   *   wb : kesme/nokta/tire silinmis kelimeler ("L'Interdit" -> ["linterdit"])
   *   qz : bosluksuz tum metin; "bleudechanel" gibi bitisik yazimlar icin
   */
  static anahtarlar(x) {
    const ham = x.m + ' ' + x.b + ' ' + x.c;
    const q = KokuArama.sadelestir(ham);
    const qb = KokuArama.sadelestir(String(ham).replace(/['’‘`´.\-]/g, ''));
    const wo = [...new Set((q + ' ' + qb).split(' ').flatMap(KokuArama.okunuslar))];
    return { q, w: q.split(' '), wb: qb.split(' '), wo, qz: q.replace(/ /g, ''), mq: KokuArama.sadelestir(x.m) };
  }

  /**
   * Bir kelimenin olasi okunuslari. Ayni ad farkli dillerde farkli okunur;
   * musteri hangisini duyduysa onu yazar. Tek okunus yetmez:
   *   Charlie  -> sarli (Fransizca) / carli (Ingilizce, "çarli")
   *   Euphoria -> oforia (Fr., "öforya") / iuforia (Ing., "yuforya") / euforia (harf harf)
   *   Gucci    -> guci (Italyanca, "guçi")
   * Anahtar alfabesi: c = ç sesi, s = ş/s sesi, j = j sesi, k = sert k.
   * Girdi sadelestir()'den gecmis tek kelimedir.
   */
  static okunuslar(k) {
    const onb = KokuArama._okunusOnbellek;
    if (onb.has(k)) return onb.get(k);

    const ortak = k
      .replace(/ph/g, 'f')
      .replace(/th/g, 't')
      .replace(/ck|qu|q/g, 'k')
      .replace(/x/g, 'ks')
      .replace(/w/g, 'v')
      .replace(/z/g, 's');
    const bitir = (s, sessizE) => {
      s = s.replace(/y/g, 'i').replace(/([a-z])\1+/g, '$1');
      return sessizE && s.length > 3 ? s.replace(/e$/, '') : s;
    };

    const fransizca = bitir(
      ortak
        .replace(/^h/, '')
        .replace(/eau|eu|au/g, 'o')
        .replace(/ou/g, 'u')
        .replace(/s?ch|sh/g, 's')
        .replace(/c(?=[eiy])/g, 's')
        .replace(/c/g, 'k')
        .replace(/g(?=[eiy])/g, 'j'),
      true
    );
    const ingilizce = bitir(
      ortak
        .replace(/eu/g, 'iu')
        .replace(/au/g, 'o')
        .replace(/ee/g, 'i')
        .replace(/oo/g, 'u')
        .replace(/sh/g, 's')
        .replace(/ch/g, 'Ç') // once ç sesini isaretle, sonra kalan c'leri coz
        .replace(/c(?=[eiy])/g, 's')
        .replace(/c/g, 'k')
        .replace(/Ç/g, 'c')
        .replace(/g(?=[eiy])/g, 'j'),
      true
    );
    const italyanca = bitir(
      ortak
        .replace(/([a-z])\1+/g, '$1')
        .replace(/sc(?=[ei])/g, 's')
        .replace(/ch/g, 'k')
        .replace(/c(?=[ei])/g, 'Ç') // Italyanca ce/ci = çe/çi
        .replace(/c/g, 'k')
        .replace(/Ç/g, 'c')
        .replace(/g(?=[ei])/g, 'j')
        .replace(/ji(?=[aou])/g, 'j'),
      false
    );
    const harfHarf = bitir(ortak.replace(/c/g, 'k'), false);

    const sonuc = [...new Set([fransizca, ingilizce, italyanca, harfHarf])];
    onb.set(k, sonuc);
    return sonuc;
  }

  /**
   * Musterinin yazdigi kelimenin okunuslari. Turkce yazim zaten okunusa yakin,
   * ama sadelestir() "ç"yi "c"ye cevirdigi ve musteri telefonda Turkce harf
   * kullanmayabildigi icin "c" uc turlu denenir: ç (carli), k (kod), j (corcio).
   */
  static sorguOkunuslari(w) {
    const onb = KokuArama._sorguOnbellek;
    if (onb.has(w)) return onb.get(w);
    const ortak = w
      .replace(/ph/g, 'f')
      .replace(/ck|qu|q/g, 'k')
      .replace(/x/g, 'ks')
      .replace(/w/g, 'v')
      .replace(/y/g, 'i')
      .replace(/([a-z])\1+/g, '$1');
    const sonuc = [...new Set([ortak, ortak.replace(/c/g, 'k'), ortak.replace(/c/g, 'j')])].filter(
      (s) => s.length >= 3
    );
    onb.set(w, sonuc);
    return sonuc;
  }

  /** Turkce uyumlu arama anahtari: kucult, aksan sok, noktalamayi bosluga cevir. */
  static sadelestir(s) {
    return String(s)
      .toLocaleLowerCase('tr')
      .replace(/ı/g, 'i')
      .replace(/ş/g, 's')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /* ---------- Arama ---------- */

  girdi() {
    this.toggleAttribute('data-dolu', this.input.value.length > 0);
    clearTimeout(this.zamanlayici);
    this.zamanlayici = setTimeout(() => this.ara(), 120);
  }

  ara() {
    const ham = this.input.value.trim();
    if (ham.length < 2) return this.kapat();

    // Veri henüz inmediyse panel boş kalmasın; kullanıcı bir şey olduğunu görsün.
    if (!this.veri) this.bekleGoster();

    // Sıra güvencesi: geç dönen eski bir istek yeni sonucun üstüne yazmasın.
    const sira = ++this.sira;
    this.veriYukle()
      .then(() => {
        if (sira !== this.sira) return;
        this.goster(this.eslesenler(ham), ham);
      })
      .catch(() => {
        if (sira === this.sira) this.hataGoster();
      });
  }

  bekleGoster() {
    this.panel.innerHTML =
      '<li class="koku-yol__durum" role="option" aria-selected="false"><p>' +
      KokuArama.kacis(this.dataset.bekleMetin || 'Aranıyor…') +
      '</p></li>';
    this.ac();
  }

  /**
   * Damerau-Levenshtein mesafesi. Yer degistirmeyi tek hata sayar; "bleu"/"blue"
   * gibi cok yapilan hatalar bu sayede 1 mesafede kalir.
   */
  static mesafe(a, b, tavan) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > tavan) return tavan + 1;
    let onceki = [];
    let simdi = [];
    let ikiOnceki = [];
    for (let j = 0; j <= b.length; j++) onceki[j] = j;
    for (let i = 1; i <= a.length; i++) {
      simdi = [i];
      let satirEnAz = i;
      for (let j = 1; j <= b.length; j++) {
        const bedel = a[i - 1] === b[j - 1] ? 0 : 1;
        let d = Math.min(onceki[j] + 1, simdi[j - 1] + 1, onceki[j - 1] + bedel);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          d = Math.min(d, ikiOnceki[j - 2] + 1);
        }
        simdi[j] = d;
        if (d < satirEnAz) satirEnAz = d;
      }
      if (satirEnAz > tavan) return tavan + 1; // erken cikis
      ikiOnceki = onceki;
      onceki = simdi;
    }
    return onceki[b.length];
  }

  /** Kelime uzunluguna gore kabul edilebilir hata payi. */
  static tolerans(w) {
    if (w.length <= 3) return 0;
    if (w.length <= 6) return 1;
    return 2;
  }

  /**
   * Bir kaydin sorgu kelimesini icerip icermedigi.
   * - Kelime basi her uzunlukta gecerli: "bl" -> "bleu", "sau" -> "sauvage".
   *   Eskiden 1-3 harf tam kelime istiyordu; musteri 4. harfe kadar sonuc goremiyordu.
   * - Kelime ici eslesme 3 harften itibaren (tek harf her kelimede gecer).
   * - Kesme/nokta/tire yok sayilir: "linterdit", "dont be shy", "bleudechanel".
   */
  /** Her sorgu kelimesi bir kelimenin BASINDA mi geciyor (kelime ortasinda degil)? */
  static onEkMi(kayit, kelimeler) {
    return kelimeler.every((w) => {
      const bas = (kw) => kw.startsWith(w);
      return kayit.w.some(bas) || (kayit.wb && kayit.wb.some(bas));
    });
  }

  static gecerMi(kayit, w, fuzzy) {
    const bas = (kw) => kw.startsWith(w);
    if (kayit.w.some(bas) || (kayit.wb && kayit.wb.some(bas))) return 0;
    if (w.length >= 3 && (kayit.q.includes(w) || (kayit.qz && kayit.qz.includes(w)))) return 0;
    // Okunusa gore: birebir yazimin biraz gerisinde siralansin diye 0.5 hata sayilir.
    // Kayidin tum okunuslari (Fr./Ing./It./harf harf) x sorgunun tum okunuslari.
    // 3 harfin altindaki anahtarlar sorguOkunuslari()'nda zaten elenir.
    const wo = w.length >= 3 && kayit.wo ? KokuArama.sorguOkunuslari(w) : [];
    if (wo.some((s) => kayit.wo.some((kw) => kw.startsWith(s)))) return 0.5;
    if (!fuzzy) return -1;
    const tol = KokuArama.tolerans(w);
    if (!tol) return -1;
    let enIyi = -1;
    for (const kw of kayit.wb ? kayit.w.concat(kayit.wb) : kayit.w) {
      const d = KokuArama.mesafe(w, kw, tol);
      if (d <= tol && (enIyi === -1 || d < enIyi)) enIyi = d;
      if (enIyi === 1) break;
    }
    // Okunus anahtarlarina ayrica yazim hatasi toleransi UYGULANMAZ: okunus
    // zaten esneklik sagliyor, ikisi ust uste binince "carli" -> "marli"
    // (Parfums de Marly) gibi alakasiz sonuclar cikiyordu.
    return enIyi;
  }

  eslesenler(ham) {
    const sorgu = KokuArama.sadelestir(ham);
    if (!sorgu) return [];
    const kelimeler = sorgu.split(' ');

    const puanla = (k, fuzzy) => {
      // Butun kelimeler gecmeli; boylece "chanel bleu" ile "bleu de chanel" eslesir
      let hata = 0;
      for (const w of kelimeler) {
        const d = KokuArama.gecerMi(k, w, fuzzy);
        if (d < 0) return -1;
        hata += d;
      }
      let p;
      if (k.mq === sorgu) p = 100;
      else if (k.mq.startsWith(sorgu)) p = 80;
      else if (k.mq.includes(sorgu)) p = 60;
      else if (k.q.startsWith(sorgu)) p = 50;
      else p = 30;
      if (k.stokta) p += 15; // stoklu urun her zaman one cikar
      if (KokuArama.onEkMi(k, kelimeler)) p += 12; // "ble" -> "Bleu", "Irresistible"dan once
      return p - hata * 12 - Math.min(k.mq.length / 20, 5);
    };

    const tara = (fuzzy) =>
      this.veri
        .map((k) => ({ k, p: puanla(k, fuzzy) }))
        .filter((x) => x.p >= 0)
        .sort((a, b) => b.p - a.p);

    // Once birebir ara. Yeterli sonuc yoksa yazim hatasina toleransli tekrar dene:
    // hem musteri yanlis yazmis olabilir hem de listedeki kayit hatali olabilir.
    let bulunan = tara(false);
    if (!bulunan.length) {
      bulunan = tara(true);
    } else {
      // Birebir sonuc olsa bile stoktaki urunler yazim farkiyla da gorunsun:
      // "bleu" yazan musteri, listede "Blue De Chanel" diye kayitli stoktaki
      // urunu kacirmasin. Sadece stoklular eklenir; ozel uretim gurultusu artmaz.
      const ekler = tara(true).filter((x) => x.k.stokta && !bulunan.some((b) => b.k === x.k));
      // Sona eklenir: birebir bulunanlarin onune gecmez ("sarli" -> once Charlie).
      if (ekler.length) bulunan = bulunan.concat(ekler.sort((a, b) => b.p - a.p));
    }
    return bulunan.slice(0, 8).map((x) => x.k);
  }

  /* ---------- Gosterim ---------- */

  goster(liste, ham) {
    this.secili = -1;
    this.panel.innerHTML = liste.length
      ? liste.map((k, i) => this.satir(k, ham, i)).join('')
      : this.bosDurum(ham);
    this.ac();
    this.duyur(
      liste.length
        ? liste.length + ' sonuc bulundu, ' + liste.filter((k) => k.stokta).length + ' tanesi stokta.'
        : 'Sonuc bulunamadi.'
    );
  }

  satir(k, ham, i) {
    const url = k.stokta ? this.dataset.urunKok + k.h : this.dataset.ozelUrl;
    const rozet = k.stokta ? this.dataset.rozetStok : this.dataset.rozetOzel;
    const sinif = k.stokta ? 'stok' : 'ozel';
    return (
      '<li class="koku-yol__sonuc" role="option" id="koku-sec-' + i + '" aria-selected="false">' +
        '<a href="' + KokuArama.kacis(url) + '" tabindex="-1">' +
          '<span class="koku-yol__ad">' +
            '<span class="koku-yol__model">' + KokuArama.vurgula(k.m, ham) + '</span>' +
            '<span class="koku-yol__marka">' + KokuArama.kacis(k.b) + (k.n ? ' &middot; Niş' : '') + '</span>' +
          '</span>' +
          '<span class="koku-yol__rozet koku-yol__rozet--' + sinif + '">' + KokuArama.kacis(rozet) + '</span>' +
        '</a>' +
      '</li>'
    );
  }

  bosDurum(ham) {
    return (
      '<li class="koku-yol__durum" role="option" aria-selected="false">' +
        '<p><strong>&quot;' + KokuArama.kacis(ham) + '&quot;</strong> ' + KokuArama.kacis(this.dataset.bulunamadi) + '</p>' +
        '<a class="button" href="' + KokuArama.kacis(this.dataset.talepUrl) + '">' +
          KokuArama.kacis(this.dataset.talepMetin) +
        '</a>' +
      '</li>'
    );
  }

  hataGoster() {
    this.panel.innerHTML =
      '<li class="koku-yol__durum" role="option" aria-selected="false"><p>' +
      KokuArama.kacis(this.dataset.hataMetin) +
      '</p></li>';
    this.ac();
  }

  static kacis(s) {
    const harita = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => harita[c]);
  }

  /** Eslesen harfleri isaretler. Once HTML kacisi yapilir, sonra <mark> eklenir. */
  static vurgula(metin, ham) {
    const guvenli = KokuArama.kacis(metin);
    const kelimeler = KokuArama.sadelestir(ham).split(' ').filter(Boolean);
    if (!kelimeler.length) return guvenli;
    const desen = kelimeler
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .sort((a, b) => b.length - a.length)
      .join('|');
    return guvenli.replace(new RegExp('(' + desen + ')', 'gi'), '<mark>$1</mark>');
  }

  /* ---------- Panel durumu ---------- */

  ac() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  kapat() {
    this.panel.hidden = true;
    this.panel.innerHTML = '';
    this.secili = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  temizle() {
    this.input.value = '';
    this.removeAttribute('data-dolu');
    this.kapat();
    this.input.focus();
  }

  duyur(metin) {
    this.canli.textContent = metin;
  }

  /* ---------- Klavye ---------- */

  klavye(e) {
    const secenekler = this.panel.querySelectorAll('[role="option"]');
    if (e.key === 'Escape') return this.temizle();
    if (!secenekler.length) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const yon = e.key === 'ArrowDown' ? 1 : -1;
      this.secili = (this.secili + yon + secenekler.length) % secenekler.length;
      secenekler.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.secili)));
      const aktif = secenekler[this.secili];
      this.input.setAttribute('aria-activedescendant', aktif.id || '');
      aktif.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.secili > -1) {
      const bag = secenekler[this.secili].querySelector('a');
      if (bag) {
        e.preventDefault();
        bag.click();
      }
    }
  }
}

KokuArama._okunusOnbellek = new Map();
KokuArama._sorguOnbellek = new Map();
customElements.define('koku-arama', KokuArama);
